import { useState, useRef, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Mail, ArrowRight, ChevronLeft, X } from 'lucide-react'
import { QMark } from '../components/Sidebar'

/* ── Device detection ── */
function getDeviceInfo() {
  const ua = navigator.userAgent
  const isIOS        = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
  const isAndroid    = /Android/.test(ua)
  const isMobile     = isIOS || isAndroid || window.innerWidth < 768
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  return { isIOS, isAndroid, isMobile, isStandalone }
}

/* ── iOS install banner ── */
function IOSBanner({ onDismiss }) {
  return (
    <div style={{ background:'linear-gradient(135deg,rgba(0,212,170,.1),rgba(0,212,170,.05))', border:'1px solid rgba(0,212,170,.3)', borderRadius:14, padding:'14px 16px', marginBottom:20, position:'relative' }}>
      <button onClick={onDismiss} style={{ position:'absolute', top:10, right:10, background:'none', border:'none', cursor:'pointer', color:'var(--slate-dim)', padding:4 }}>
        <X size={14} />
      </button>
      <div style={{ fontSize:13, fontWeight:600, color:'var(--teal)', marginBottom:10, paddingRight:20 }}>
        Add Quarterly to your home screen
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {[
          { n:1, text:'Tap the Share button at the bottom of Safari', icon:'⬆️' },
          { n:2, text:'Scroll down and tap "Add to Home Screen"',     icon:'➕' },
          { n:3, text:'Tap "Add" — it sits on your phone like any app', icon:'✓' },
        ].map(s => (
          <div key={s.n} style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:22, height:22, borderRadius:'50%', background:'rgba(0,212,170,.15)', border:'1px solid rgba(0,212,170,.25)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <span style={{ fontSize:11, fontWeight:700, color:'var(--teal)' }}>{s.n}</span>
            </div>
            <span style={{ fontSize:12, color:'var(--slate)', lineHeight:1.5 }}>{s.icon} {s.text}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop:10, fontSize:11, color:'var(--slate-dim)', lineHeight:1.5 }}>
        Once installed, sign in from the app — no browser, no Outlook redirects, stays logged in.
      </div>
    </div>
  )
}

/* ── Android install banner ── */
function AndroidBanner({ onDismiss }) {
  return (
    <div style={{ background:'linear-gradient(135deg,rgba(0,212,170,.1),rgba(0,212,170,.05))', border:'1px solid rgba(0,212,170,.3)', borderRadius:14, padding:'14px 16px', marginBottom:20, position:'relative' }}>
      <button onClick={onDismiss} style={{ position:'absolute', top:10, right:10, background:'none', border:'none', cursor:'pointer', color:'var(--slate-dim)', padding:4 }}>
        <X size={14} />
      </button>
      <div style={{ fontSize:13, fontWeight:600, color:'var(--teal)', marginBottom:10, paddingRight:20 }}>
        Install Quarterly on your phone
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {[
          { n:1, text:'Tap the three dots ⋮ in the top right of Chrome', icon:'⋮' },
          { n:2, text:'Tap "Add to Home screen" or "Install app"',       icon:'➕' },
          { n:3, text:'Tap "Install" — it sits alongside your other apps', icon:'✓' },
        ].map(s => (
          <div key={s.n} style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:22, height:22, borderRadius:'50%', background:'rgba(0,212,170,.15)', border:'1px solid rgba(0,212,170,.25)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <span style={{ fontSize:11, fontWeight:700, color:'var(--teal)' }}>{s.n}</span>
            </div>
            <span style={{ fontSize:12, color:'var(--slate)', lineHeight:1.5 }}>{s.icon} {s.text}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop:10, fontSize:11, color:'var(--slate-dim)', lineHeight:1.5 }}>
        Chrome may also show an install prompt at the bottom automatically.
      </div>
    </div>
  )
}

/* ── Main Login ── */
export default function Login() {
  const { isIOS, isAndroid, isMobile, isStandalone } = getDeviceInfo()

  const [step, setStep]       = useState('email')
  const [email, setEmail]     = useState('')
  const [otp, setOtp]         = useState(['','','','','',''])
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [resendTimer, setResendTimer] = useState(0)
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem('q_install_dismissed') === '1'
  )

  const inputRefs = useRef([])

  // Show banner: mobile + in browser (not standalone) + not dismissed + on email step
  const showBanner = isMobile && !isStandalone && !bannerDismissed && step === 'email'

  function dismissBanner() {
    localStorage.setItem('q_install_dismissed', '1')
    setBannerDismissed(true)
  }

  // Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return
    const t = setTimeout(() => setResendTimer(r => r - 1), 1000)
    return () => clearTimeout(t)
  }, [resendTimer])

  // Send OTP
  async function sendOtp(e) {
    e?.preventDefault()
    if (!email || loading) return
    setLoading(true); setError('')

    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
      // No emailRedirectTo → Supabase sends a 6-digit code, NOT a magic link
    })

    setLoading(false)
    if (err) {
      setError('Could not send the code. Please check your email and try again.')
    } else {
      setStep('otp')
      setResendTimer(30)
      setTimeout(() => inputRefs.current[0]?.focus(), 150)
    }
  }

  // OTP digit input
  function onOtpChange(i, value) {
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...otp]; next[i] = digit; setOtp(next); setError('')
    if (digit && i < 5) inputRefs.current[i + 1]?.focus()
    if (digit && i === 5 && next.join('').length === 6) verifyOtp(next.join(''))
  }

  function onOtpKeyDown(i, e) {
    if (e.key === 'Backspace') {
      if (otp[i]) { const n=[...otp]; n[i]=''; setOtp(n) }
      else if (i > 0) { inputRefs.current[i-1]?.focus(); const n=[...otp]; n[i-1]=''; setOtp(n) }
    }
    if (e.key === 'ArrowLeft'  && i > 0) inputRefs.current[i-1]?.focus()
    if (e.key === 'ArrowRight' && i < 5) inputRefs.current[i+1]?.focus()
  }

  function onOtpPaste(e) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const next = Array(6).fill('').map((_, i) => pasted[i] || '')
    setOtp(next)
    inputRefs.current[Math.min(pasted.length, 5)]?.focus()
    if (pasted.length === 6) verifyOtp(pasted)
  }

  // Verify OTP with Supabase
  async function verifyOtp(code) {
    if (loading) return
    setLoading(true); setError('')
    const { error: err } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' })
    setLoading(false)
    if (err) {
      setError('That code is wrong or expired. Try again or request a new one.')
      setOtp(['','','','','',''])
      setTimeout(() => inputRefs.current[0]?.focus(), 50)
    }
    // Success → AppContext onAuthStateChange fires → LoginGuard redirects
  }

  function handleSubmitOtp(e) {
    e.preventDefault()
    const code = otp.join('')
    if (code.length === 6) verifyOtp(code)
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--navy)', padding: 20,
      paddingTop:    'max(20px, env(safe-area-inset-top))',
      paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
    }}>
      <div style={{ width: '100%', maxWidth: 400 }} className="fade-up">

        {/* Install banners — only on mobile browsers */}
        {showBanner && isIOS     && <IOSBanner     onDismiss={dismissBanner} />}
        {showBanner && isAndroid && <AndroidBanner onDismiss={dismissBanner} />}

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ display: 'inline-flex', marginBottom: 14 }}>
            <QMark size={52} variant="teal" />
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 6 }}>
            {step === 'email' ? 'Sign in to Quarterly' : 'Enter your code'}
          </h1>
          <p style={{ color: 'var(--slate)', fontSize: 13, lineHeight: 1.65 }}>
            {step === 'email'
              ? "We'll send a 6-digit code to your email. No password needed."
              : <span>We sent a code to <strong style={{ color:'var(--white)' }}>{email}</strong></span>
            }
          </p>
        </div>

        {/* Plan reminder */}
        {step === 'email' && (
          <div style={{ background:'rgba(0,212,170,.06)', border:'1px solid rgba(0,212,170,.2)', borderRadius:'var(--r)', padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--white)' }}>Quarterly — £14.99/month</div>
              <div style={{ fontSize:11, color:'var(--slate)', marginTop:2 }}>Cancel anytime · No card required to preview</div>
            </div>
            <span className="badge badge-teal" style={{ fontSize:10, whiteSpace:'nowrap' }}>Selected</span>
          </div>
        )}

        <div className="card">

          {/* Email step */}
          {step === 'email' && (
            <form onSubmit={sendOtp} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div className="field">
                <label>Your email address</label>
                <div style={{ position:'relative' }}>
                  <Mail size={14} style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', color:'var(--slate-dim)', pointerEvents:'none' }} />
                  <input
                    type="email" value={email}
                    onChange={e => { setEmail(e.target.value); setError('') }}
                    placeholder="you@example.com"
                    required autoFocus
                    autoComplete="email" inputMode="email"
                    style={{ paddingLeft:38, fontSize:16 }}
                  />
                </div>
              </div>
              {error && <div className="alert alert-red" style={{ fontSize:13 }}>{error}</div>}
              <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading || !email}>
                {loading ? <span className="spin" /> : <>Send me a code <ArrowRight size={16} /></>}
              </button>
            </form>
          )}

          {/* OTP step */}
          {step === 'otp' && (
            <form onSubmit={handleSubmitOtp} style={{ display:'flex', flexDirection:'column', gap:20 }}>
              <div>
                <div style={{ fontSize:12, color:'var(--slate)', marginBottom:12, fontWeight:500 }}>6-digit code</div>
                <div style={{ display:'flex', gap:8, justifyContent:'center' }} onPaste={onOtpPaste}>
                  {otp.map((digit, i) => (
                    <input key={i} ref={el => inputRefs.current[i] = el}
                      type="text" inputMode="numeric" pattern="[0-9]*"
                      maxLength={1} value={digit}
                      onChange={e => onOtpChange(i, e.target.value)}
                      onKeyDown={e => onOtpKeyDown(i, e)}
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      style={{
                        width:44, height:54, textAlign:'center', fontSize:22, fontWeight:700,
                        padding:0, letterSpacing:0, fontFamily:'var(--font-body)',
                        border:`2px solid ${digit ? 'var(--teal)' : 'var(--navy-border)'}`,
                        borderRadius:10,
                        background: digit ? 'rgba(0,212,170,.06)' : 'var(--navy-border)',
                        color:'var(--white)', outline:'none', caretColor:'var(--teal)',
                        transition:'border-color .15s, background .15s',
                      }}
                    />
                  ))}
                </div>
              </div>

              {error && <div className="alert alert-red" style={{ fontSize:13 }}>{error}</div>}

              {loading && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                  <span className="spin" />
                  <span style={{ fontSize:13, color:'var(--slate)' }}>Verifying...</span>
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading || otp.join('').length < 6}>
                {loading ? <span className="spin" /> : <>Confirm <ArrowRight size={16} /></>}
              </button>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:-8 }}>
                <button type="button"
                  onClick={() => { setStep('email'); setOtp(['','','','','','']); setError('') }}
                  style={{ background:'none', border:'none', color:'var(--slate)', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:4, padding:0 }}>
                  <ChevronLeft size={12} /> Change email
                </button>
                {resendTimer > 0
                  ? <span style={{ fontSize:12, color:'var(--slate-dim)' }}>Resend in {resendTimer}s</span>
                  : <button type="button" onClick={() => sendOtp()}
                      style={{ background:'none', border:'none', color:'var(--teal)', fontSize:12, cursor:'pointer', fontWeight:600, padding:0 }}>
                      Resend code
                    </button>
                }
              </div>
            </form>
          )}
        </div>

        {/* Security note */}
        <div style={{ marginTop:16, padding:'12px 14px', background:'rgba(0,212,170,.04)', borderRadius:10, border:'1px solid rgba(0,212,170,.1)', fontSize:12, color:'var(--slate)', display:'flex', alignItems:'flex-start', gap:8 }}>
          <span style={{ fontSize:14, flexShrink:0 }}>🔒</span>
          <span>No password required. A fresh 6-digit code each time — faster and more secure than a link.</span>
        </div>

        <p style={{ textAlign:'center', fontSize:11, color:'var(--slate-dim)', marginTop:16 }}>
          New to Quarterly? Enter your email above — account created automatically.
        </p>
      </div>
    </div>
  )
}
